import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const referralsApi = createApi({
  reducerPath: "referrals",
  tagTypes: ["referrals"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getReferrals: builder.query<ApiTypes.Res.Referrals, UITypes.Params>({
      query: arg => {
        return {
          url: `/referrals`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["referrals"],
    }),
    getArchivedReferrals: builder.query<ApiTypes.Res.Referrals, UITypes.Params>({
      query: arg => {
        return {
          url: `/referrals/archived`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["referrals"],
    }),
    getReferral: builder.query<ApiTypes.Res.Referral, number>({
      query: id => {
        return {
          url: `/referrals/${id}`,
          method: "GET",
        }
      },
    }),
    createReferral: builder.mutation<ApiTypes.Res.Referral, ApiTypes.Req.CreateReferral>({
      query: body => {
        return {
          url: `/referrals`,
          method: "POST",
          body,
        }
      },
      invalidatesTags: ["referrals"],
    }),
    updateReferral: builder.mutation<ApiTypes.Res.Referral, { id: number; body: ApiTypes.Req.CreateReferral }>({
      query: data => {
        return {
          url: `/referrals/${data.id}`,
          method: "PUT",
          body: data.body,
        }
      },
      invalidatesTags: ["referrals"],
    }),
    confirmReferral: builder.mutation<void, number>({
      query: id => {
        return {
          url: `/referrals/${id}/confirm`,
          method: "POST",
        }
      },
      invalidatesTags: ["referrals"],
    }),
    denyReferral: builder.mutation<void, number>({
      query: id => {
        return {
          url: `/referrals/${id}/deny`,
          method: "POST",
        }
      },
      invalidatesTags: ["referrals"],
    }),
    getAdminReferrals: builder.query<ApiTypes.Res.VendorReferralAgents, UITypes.Params>({
      query: arg => {
        return {
          url: `/admin/referrals`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getAdminReferral: builder.query<ApiTypes.Res.Referral, number>({
      query: id => {
        return {
          url: `/admin/referrals/${id}`,
          method: "GET",
        }
      },
    }),
    assignReferral: builder.query<ApiTypes.Res.ReferralAssignment, { id: number; body: ApiTypes.Req.AssignReferral }>({
      query: data => {
        return {
          url: `/admin/referrals/${data.id}/assign`,
          method: "PUT",
          body: data.body,
        }
      },
    }),
  }),
})

export const {
  useLazyGetReferralsQuery,
  useLazyGetArchivedReferralsQuery,
  useLazyGetReferralQuery,
  useCreateReferralMutation,
  useUpdateReferralMutation,
  useConfirmReferralMutation,
  useDenyReferralMutation,
  useLazyGetAdminReferralsQuery,
  useLazyGetAdminReferralQuery,
  useLazyAssignReferralQuery,
} = referralsApi
